import { NAVIGATION } from '../navigation';
import { PREFIXE_FAMILLE } from '@kn/shared';

/** Écrans qui ne figurent pas dans la navigation latérale. */
const HORS_MENU: Record<string, string> = {
  '/connexion': 'Connexion',
  '/changer-mot-de-passe': 'Changer le mot de passe',
  '/notes-de-version': 'Notes de version',
};

const ENTREES = NAVIGATION.flatMap((section) =>
  section.entries.map((entree) => ({
    ...entree,
    /** Le module dont l'entrée dépend, pour les entrées rattachées. */
    section: section.label,
  })),
);

/**
 * Le titre que porte une adresse.
 *
 * Déduit de la navigation plutôt que déclaré écran par écran : une entrée
 * ajoutée au menu obtient son titre sans que personne ait à y penser, et aucun
 * écran ne peut rester anonyme par oubli.
 *
 * La correspondance se fait sur le **préfixe le plus long** — `/restant` et
 * `/restant/historique` sont deux écrans, et c'est le second qui doit gagner sur
 * sa propre adresse.
 */
export function titreDeLaRoute(chemin: string): string | null {
  const horsMenu = HORS_MENU[chemin];
  if (horsMenu) return horsMenu;

  // La fiche d'un écart porte **ce qu'on est venu voir** : le code article, ou
  // la famille. C'est l'écran qu'on ouvre le plus en plusieurs onglets à la
  // fois — un titre générique y serait exactement inutile.
  const ecart = /^\/ecarts\/(.+)$/.exec(chemin);
  if (ecart) {
    const segment = decodeURIComponent(ecart[1]!);
    // L'adresse d'une famille porte un UUID : il n'apprend rien a personne dans
    // une barre d'onglets. La fiche annoncera son nom des qu'elle l'aura.
    return segment.startsWith(PREFIXE_FAMILLE) ? 'Famille' : `Écart ${segment}`;
  }

  const candidats = ENTREES.filter(
    (entree) => chemin === entree.path || chemin.startsWith(`${entree.path}/`),
  ).sort((a, b) => b.path.length - a.path.length);

  const entree = candidats[0];
  if (!entree) return null;

  // « Historique » seul ne dit rien : dans un onglet, il lui faut son module.
  return entree.rattachee && entree.section === null
    ? `${entree.label} — ${moduleParent(entree.path)}`
    : entree.label;
}

/** Le libellé de l'entrée dont dépend une entrée rattachée. */
function moduleParent(chemin: string): string {
  const parent = chemin.slice(0, chemin.lastIndexOf('/'));
  return ENTREES.find((entree) => entree.path === parent)?.label ?? '';
}
